import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  User,
  IndianRupee,
  MapPin,
  Receipt,
  Navigation,
  History,
  Loader2,
  Info,
  Car,
  Phone,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { toast } from "sonner";
import Swal from "sweetalert2";
import api from "../api/Api";

const DmrPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [activeTrips, setActiveTrips] = useState([]);
  const [tripsLoading, setTripsLoading] = useState(false);
  const [autoFetchedData, setAutoFetchedData] = useState(null);

  // Accordion States for Pre-recorded Expenses
  const [showRefuelDetails, setShowRefuelDetails] = useState(false);
  const [showAdvanceDetails, setShowAdvanceDetails] = useState(false);
  const [showTollDetails, setShowTollDetails] = useState(false);
  const [showParkingDetails, setShowParkingDetails] = useState(false);
  const [showNightDetails, setShowNightDetails] = useState(false);
  const [showFoodDetails, setShowFoodDetails] = useState(false);

  // ADD THESE NEW STATES FOR DRIVER CASH
  const [showCashFuelDetails, setShowCashFuelDetails] = useState(false);
  const [showCashTollDetails, setShowCashTollDetails] = useState(false);
  const [showCashParkingDetails, setShowCashParkingDetails] = useState(false);
  const [showCashNightDetails, setShowCashNightDetails] = useState(false);
  const [showCashOtherDetails, setShowCashOtherDetails] = useState(false);

  // Accordion States for Final Settlement
  const [showCollectionDetails, setShowCollectionDetails] = useState(false);
  const [showExpenseDetails, setShowExpenseDetails] = useState(false);

  const [form, setForm] = useState({
    trip_id: "",
    from_guest: "",
    start_km: "",
    end_km: "",
    oil_rate: "",
    parking_amnt: "",
    night_amnt: "",
    food_amnt: "",
    toll_amnt: "",
    misc1: "",
    miscval1: "",
    misc2: "",
    miscval2: "",
  });

  const [calc, setCalc] = useState({
    total_km: 0,
    fuel_amnt: 0,
    tot_amnt: 0,
    tot_frmguest: 0,
    paid_ofc: 0,
    total_cash_exp: 0,
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
    });
  };

  useEffect(() => {
    const fetchActiveTrips = async () => {
      const driverRegNo = localStorage.getItem("driverRegNo");
      if (!driverRegNo) return;
      setTripsLoading(true);
      try {
        const response = await api.get(
          `/dmr/select-trips-dmr?driver_regno=${driverRegNo}`,
        );
        setActiveTrips(response.data || []);
      } catch (error) {
        console.error("Trip fetch error", error);
        setActiveTrips([]);
      } finally {
        setTripsLoading(false);
      }
    };
    fetchActiveTrips();
  }, []);

  const handleTripChange = (e) => {
    const selectedId = e.target.value;
    const trip = activeTrips.find(
      (t) => String(t.enq_id) === String(selectedId),
    );

    if (trip) {
      setAutoFetchedData(trip);
      setForm({
        ...form,
        trip_id: selectedId,
        start_km: trip.start_km > 0 ? trip.start_km : "",
        end_km: trip.end_km > 0 ? trip.end_km : "",
        duty_adv: trip.advance_amnt > 0 ? trip.advance_amnt : "",
        parking_amnt: trip.parking_amnt > 0 ? trip.parking_amnt : "",
        night_amnt: trip.night_amnt > 0 ? trip.night_amnt : "",
        food_amnt: trip.other_amnt > 0 ? trip.other_amnt : "",
        toll_amnt: trip.toll_amnt > 0 ? trip.toll_amnt : "",
      });
    } else {
      setAutoFetchedData(null);
      setShowRefuelDetails(false);
      setShowAdvanceDetails(false);
      setShowTollDetails(false);
      setShowParkingDetails(false);
      setShowNightDetails(false);
      setShowFoodDetails(false);
      setShowCashFuelDetails(false);
      setShowCashTollDetails(false);
      setShowCashParkingDetails(false);
      setShowCashNightDetails(false);
      setShowCashOtherDetails(false);
      setShowCollectionDetails(false);
      setShowExpenseDetails(false);

      setForm({
        trip_id: "",
        from_guest: "",
        start_km: "",
        end_km: "",
        oil_rate: "",
        parking_amnt: "",
        night_amnt: "",
        food_amnt: "",
        toll_amnt: "",
        misc1: "",
        miscval1: "",
        misc2: "",
        miscval2: "",
      });
    }
  };

  // Full Dynamic Calculation Logic
  useEffect(() => {
    const start = parseFloat(form.start_km) || 0;
    const end = parseFloat(form.end_km) || 0;
    const oil = parseFloat(form.oil_rate) || 0;

    // User Input Values
    const parking = parseFloat(form.parking_amnt) || 0;
    const night = parseFloat(form.night_amnt) || 0;
    const food = parseFloat(form.food_amnt) || 0;
    const toll = parseFloat(form.toll_amnt) || 0;
    const guestCol = parseFloat(form.from_guest) || 0;

    const safeMileage = parseFloat(autoFetchedData?.millege) || 0;
    const misc1Amt = parseFloat(form.miscval1) || 0;
    const misc2Amt = parseFloat(form.miscval2) || 0;

    // DB Fetched values for Approved/Pre-Paid Expenses
    const dbAdvance = parseFloat(autoFetchedData?.advance_amnt) || 0;
    const dbRefuel = parseFloat(autoFetchedData?.fuel_amnt) || 0;
    const dbToll = parseFloat(autoFetchedData?.toll_amnt) || 0;
    const dbParking = parseFloat(autoFetchedData?.parking_amnt) || 0;
    const dbNight = parseFloat(autoFetchedData?.night_amnt) || 0;
    const dbFood = parseFloat(autoFetchedData?.other_amnt) || 0;

    // DB Fetched values for DRIVER CASH (Paid by Driver)
    const cashFuel = parseFloat(autoFetchedData?.driver_cash_fuel_amnt) || 0;
    const cashToll = parseFloat(autoFetchedData?.driver_cash_toll_amnt) || 0;
    const cashParking =
      parseFloat(autoFetchedData?.driver_cash_parking_amnt) || 0;
    const cashNight = parseFloat(autoFetchedData?.driver_cash_night_amnt) || 0;
    const cashOther = parseFloat(autoFetchedData?.driver_cash_other_amnt) || 0;

    const totalCashExp = cashToll + cashParking + cashNight + cashOther;
    const totalKm = Math.max(0, end - start);

    // 1. Calculate Fuel Expense based on formula
    const fuelExp =
      safeMileage > 0 && totalKm > 0 && oil > 0
        ? (totalKm / safeMileage) * oil
        : 0;

    // 3. Total Expenses (Calculated Fuel + Manual Inputs + ALL DRIVER CASH)
    const totalExp = fuelExp + misc1Amt + misc2Amt + totalCashExp;

    // 4. Total Pre-Approved Advance = Duty Advance + ALL pre-recorded database values
    const totalAdvance =
      dbAdvance + dbRefuel + dbToll + dbParking + dbNight + dbFood;

    // 5. Total Collection = Total Pre-Approved + Collection from Guest
    const totalCol = totalAdvance + guestCol;

    // 6. Final Paid to Office
    const paidToOffice = totalCol - totalExp;

    setCalc({
      total_km: totalKm.toFixed(1),
      fuel_amnt: fuelExp.toFixed(2),
      tot_amnt: totalExp.toFixed(2),
      tot_frmguest: totalCol.toFixed(2),
      paid_ofc: paidToOffice.toFixed(2),
    });
  }, [form, autoFetchedData]);

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!autoFetchedData) return toast.error("Please select a trip first");
    setLoading(true);
    try {
      const payload = {
        ...form,
        ...calc,
        enq_id: autoFetchedData.enq_id,
        driver_id: autoFetchedData.driver_id,
        cab_id: autoFetchedData.cab_id,
        millege: autoFetchedData.millege,
        Refuel: autoFetchedData.fuel_amnt > 0 ? autoFetchedData.fuel_amnt : "",
      //  Cash payments 
        driver_cash_fuel_amnt: autoFetchedData.driver_cash_fuel_amnt || 0,
        driver_cash_toll_amnt: autoFetchedData.driver_cash_toll_amnt || 0,
        driver_cash_parking_amnt: autoFetchedData.driver_cash_parking_amnt || 0,
        driver_cash_night_amnt: autoFetchedData.driver_cash_night_amnt || 0,
        driver_cash_other_amnt: autoFetchedData.driver_cash_other_amnt || 0,
      };

      await api.post("/dmr/add-dmr", payload);

      // Attractive Native-Style SweetAlert
      Swal.fire({
        icon: "success",
        title: "Submitted Successfully!",
        text: "Your DMR has been sent to the office.",
        confirmButtonColor: "#10b981",
        confirmButtonText: "Done",
        customClass: {
          popup: "rounded-4 shadow-lg border-0",
          confirmButton: "rounded-pill px-5 fw-bold shadow-sm",
        },
        backdrop: "rgba(0,0,0,0.4)",
      }).then(() => {
        navigate("/actions-hub");
      });
    } catch (err) {
      toast.error("Submission Failed");
    } finally {
      setLoading(false);
    }
  };

  const preventScrollChange = (e) => e.target.blur();

  const displayData = autoFetchedData || {
    driver_name: "---",
    driver_regid: "---",
    vehicle_model: "---",
    vehicle_no: "---",
    customer_name: "---",
    customer_mob: "---",
    millege: 0,
  };

  // HELPER FUNCTION: Native Styled Pre-Recorded List
  const renderPreRecordedList = (
    title,
    totalAmnt,
    detailsStr,
    showState,
    setShowState,
    color = "primary",
  ) => {
    if (!totalAmnt || totalAmnt <= 0) return null;

    const expList = detailsStr
      ? detailsStr
          .split("|")
          .map((item) => {
            const [expNo, amnt] = item.split("=");
            return { expNo: expNo?.trim(), amnt: parseFloat(amnt || 0) };
          })
          .filter((item) => item.expNo && !isNaN(item.amnt))
      : [];

    return (
      <div className="mb-3">
        {expList.length === 1 && (
          <div className="d-flex justify-content-between align-items-center py-2 border-bottom border-light">
            <div className="d-flex align-items-center gap-2">
              <span
                className="fw-semibold text-secondary"
                style={{ fontSize: "14px" }}
              >
                {title}
              </span>
              <span
                className={`badge rounded-pill bg-${color} bg-opacity-10 text-${color}`}
                style={{ fontSize: "10px" }}
              >
                {expList[0].expNo}
              </span>
            </div>
            <span className={`fw-bolder text-${color} fs-6`}>
              ₹ {expList[0].amnt.toFixed(2)}
            </span>
          </div>
        )}

        {expList.length > 1 && (
          <div
            className="rounded-4 p-2"
            style={{ backgroundColor: "#f8f9fc", border: "1px solid #e2e8f0" }}
          >
            <div
              className="d-flex justify-content-between align-items-center user-select-none py-1 px-2"
              style={{ cursor: "pointer" }}
              onClick={() => setShowState(!showState)}
            >
              <div className="d-flex align-items-center gap-2">
                <span
                  className="fw-semibold text-secondary"
                  style={{ fontSize: "14px" }}
                >
                  {title}
                </span>
                <span className="badge rounded-pill bg-secondary bg-opacity-25 text-secondary">
                  {expList.length}
                </span>
              </div>
              <div className="d-flex align-items-center gap-2">
                <span className={`fw-bolder text-${color} fs-6`}>
                  ₹ {parseFloat(totalAmnt).toFixed(2)}
                </span>
                {showState ? (
                  <ChevronUp size={18} className="text-muted" />
                ) : (
                  <ChevronDown size={18} className="text-muted" />
                )}
              </div>
            </div>
            {showState && (
              <div className="mt-2 pt-2 px-2 border-top border-secondary border-opacity-10 accordion-content">
                {expList.map((item, idx) => (
                  <div
                    key={idx}
                    className="d-flex justify-content-between align-items-center mb-2"
                  >
                    <span
                      className={`badge rounded-pill bg-${color} bg-opacity-10 text-${color} fw-medium`}
                    >
                      {item.expNo}
                    </span>
                    <span className="fw-bold text-dark small">
                      ₹ {item.amnt.toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {expList.length === 0 && (
          <div className="d-flex justify-content-between align-items-center py-2 border-bottom border-light">
            <span
              className="fw-semibold text-secondary"
              style={{ fontSize: "14px" }}
            >
              {title}
            </span>
            <span className={`fw-bolder text-${color} fs-6`}>
              ₹ {parseFloat(totalAmnt).toFixed(2)}
            </span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      className="container-fluid min-vh-100 p-0 pb-5"
      style={{
        backgroundColor: "#ffffff",
        fontFamily: "system-ui, -apple-system, sans-serif",
      }}
    >
      {/* NATIVE MOBILE STYLES */}
      <style>{`
        .native-card {
          background: #ffffff;
          border-radius: 20px;
          box-shadow: 0 2px 16px rgba(0,0,0,0.04);
          border: none;
          margin-bottom: 1rem;
          overflow: hidden;
        }
        .overlap-header { margin-top: -30px; position: relative; z-index: 10; }
        
        .native-label { 
          color: #8e8e93; 
          font-size: 11px; 
          font-weight: 600; 
          text-transform: uppercase; 
          letter-spacing: 0.5px; 
          margin-bottom: 6px; 
          display: block; 
        }
        .native-value { 
          color: #1c1c1e; 
          font-size: 15px; 
          font-weight: 600; 
        }
        
        .native-input { 
          background-color: #f2f2f7 !important; 
          border: 1.5px solid transparent !important; 
          border-radius: 14px !important; 
          padding: 12px 16px !important; 
          font-weight: 600 !important; 
          color: #1c1c1e !important; 
          font-size: 16px !important;
          transition: all 0.2s ease; 
        }
        .native-input:focus { 
          background-color: #ffffff !important; 
          border-color: #007aff !important; 
          box-shadow: 0 0 0 3px rgba(0, 122, 255, 0.15) !important; 
          outline: none; 
        }
        .native-input:disabled, .native-input[readonly] { 
          opacity: 0.8; 
          cursor: not-allowed; 
          background-color: #e5e5ea !important; 
          color: #8e8e93 !important; 
        }
        
        .native-select {
          appearance: none;
          background-image: url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%23007AFF%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E");
          background-repeat: no-repeat;
          background-position: right .7rem top 50%;
          background-size: .65rem auto;
        }

        .icon-soft { width: 36px; height: 36px; border-radius: 12px; display: flex; align-items: center; justify-content: center; }
        .input-icon-wrapper { position: relative; display: flex; align-items: center; }
        .input-icon { position: absolute; left: 1rem; color: #8e8e93; z-index: 5; }
        .input-with-icon { padding-left: 2.8rem !important; }

        input[type=number]::-webkit-inner-spin-button, 
        input[type=number]::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
        input[type=number] { -moz-appearance: textfield; }

        .accordion-content { animation: slideDown 0.25s ease-out forwards; }
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-5px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes smoothPopIn {
          0% { opacity: 0; transform: translateY(15px) scale(0.98); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        .smooth-load-cards {
          animation: smoothPopIn 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
      `}</style>

      {/* PREMIUM GLASS BANNER HEADER */}
      <div
        className="w-100 position-relative overflow-hidden shadow-sm"
        style={{
          background: "linear-gradient(135deg, #10b981 0%, #047857 100%)",
          padding: "2rem 1.5rem 3.5rem 1.5rem",
          borderBottomLeftRadius: "24px",
          borderBottomRightRadius: "24px",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: "-20px",
            left: "-20px",
            width: "120px",
            height: "120px",
            borderRadius: "50%",
            background: "rgba(255,255,255,0.1)",
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: "-40px",
            right: "-20px",
            width: "180px",
            height: "180px",
            borderRadius: "50%",
            background: "rgba(255,255,255,0.05)",
          }}
        />

        <div
          className="position-relative d-flex justify-content-between align-items-center"
          style={{ zIndex: 1 }}
        >
          <div>
            <h2
              className="text-white mb-1 fw-bold"
              style={{ fontSize: "1.8rem", letterSpacing: "-0.5px" }}
            >
              Generate DMR
            </h2>
            <p
              className="text-white mb-0 opacity-75"
              style={{ fontSize: "0.9rem" }}
            >
              {new Date().toLocaleDateString("en-IN", {
                weekday: "long",
                day: "numeric",
                month: "short",
              })}
            </p>
          </div>
          <Link
            to={`/dmr-history/${localStorage.getItem("driverRegNo")}`}
            className="btn btn-light rounded-pill px-3 py-2 fw-bold d-flex align-items-center gap-1 shadow-sm"
            style={{ color: "#047857", fontSize: "13px" }}
          >
            <History size={16} /> History
          </Link>
        </div>
      </div>

      <div
        className="container px-2 overlap-header pb-2"
        style={{ maxWidth: "600px" }}
      >
        <form onSubmit={handleSubmit}>
          {/* NATIVE TRIP SELECTOR */}
          <div className="native-card px-2 py-2">
            <select
              className="form-select native-select border-0 fw-bold shadow-none p-3 text-truncate w-100"
              style={{
                color: "#007aff",
                fontSize: "15px",
                backgroundColor: "transparent",
                cursor: "pointer",
              }}
              name="trip_id"
              value={form.trip_id}
              onChange={handleTripChange}
              disabled={tripsLoading}
            >
              <option value="" className="text-muted">
                {tripsLoading ? "Fetching Trips..." : "Select Trip for DMR..."}
              </option>
              {activeTrips.map((trip) => {
                const arrTime = trip.arrive_time_str
                  ? ` ${trip.arrive_time_str}`
                  : "";
                const deptTime = trip.dept_time_str
                  ? ` ${trip.dept_time_str}`
                  : "";
                return (
                  <option key={trip.enq_id} value={trip.enq_id}>
                    {trip.confirmation_id}({formatDate(trip.arrive_dt)}
                    {arrTime} ➔{formatDate(trip.dept_dt)}
                    {deptTime})
                  </option>
                );
              })}
            </select>
          </div>

          {autoFetchedData && (
            <div key={form.trip_id} className="smooth-load-cards">
              {/* TRIP DETAILS CARD */}
              <div className="native-card p-3">
                <div className="d-flex align-items-center gap-3 mb-2">
                  <div className="icon-soft bg-primary bg-opacity-10 text-primary">
                    <Info size={20} strokeWidth={2.5} />
                  </div>
                  <h6
                    className="mb-0 fw-bold text-dark"
                    style={{ fontSize: "17px" }}
                  >
                    Trip Details
                  </h6>
                </div>

                <div className="d-flex flex-column gap-3">
                  <div className="d-flex align-items-start gap-3 border-bottom pb-3 border-light">
                    <User size={20} className="text-secondary mt-1" />
                    <div className="flex-grow-1">
                      <span className="native-label">Driver</span>
                      <span className="native-value">
                        {displayData.driver_name}{" "}
                        <span className="text-muted fw-normal ms-1">
                          ({displayData.driver_regid})
                        </span>
                      </span>
                    </div>
                  </div>

                  <div className="d-flex align-items-start gap-3 border-bottom pb-3 border-light">
                    <Car size={20} className="text-secondary mt-1" />
                    <div className="flex-grow-1">
                      <span className="native-label">Vehicle</span>
                      <div className="d-flex align-items-center flex-wrap gap-2 mt-1">
                        <span className="native-value text-primary">
                          {displayData.vehicle_model}
                        </span>
                        <span className="badge rounded-pill bg-light text-dark border px-2 py-1">
                          {displayData.vehicle_no}
                        </span>
                        <span className="badge rounded-pill bg-light text-secondary border px-2 py-1">
                          {displayData.millege > 0
                            ? `${displayData.millege} km/l`
                            : "N/A"}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="d-flex align-items-start gap-3">
                    <Phone size={20} className="text-secondary mt-1" />
                    <div className="flex-grow-1">
                      <span className="native-label">Guest Contact</span>
                      <span className="native-value d-block">
                        {displayData.customer_name}
                      </span>
                      <span className="text-muted small fw-medium">
                        {displayData.customer_mob}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              {/* COLLECTIONS & PRE-PAID CARD */}
              <div className="native-card p-3">
                <div className="d-flex align-items-center gap-3 mb-2">
                  <div className="icon-soft bg-success bg-opacity-10 text-success">
                    <IndianRupee size={20} strokeWidth={2.5} />
                  </div>
                  <h6
                    className="mb-0 fw-bold text-dark"
                    style={{ fontSize: "17px" }}
                  >
                    Collections & Advances
                  </h6>
                </div>

                {(autoFetchedData?.advance_amnt > 0 ||
                  autoFetchedData?.fuel_amnt > 0 ||
                  autoFetchedData?.toll_amnt > 0 ||
                  autoFetchedData?.parking_amnt > 0 ||
                  autoFetchedData?.night_amnt > 0 ||
                  autoFetchedData?.other_amnt > 0) && (
                  <div className="mb-4">
                    <span className="native-label text-success mb-2">
                      Pre-Paid / Approved Allowances
                    </span>
                    <div className="p-1">
                      {renderPreRecordedList(
                        "Duty Advance",
                        autoFetchedData?.advance_amnt,
                        autoFetchedData?.advance_exp_details,
                        showAdvanceDetails,
                        setShowAdvanceDetails,
                        "success",
                      )}
                      {renderPreRecordedList(
                        "Refuel",
                        autoFetchedData?.fuel_amnt,
                        autoFetchedData?.fuel_exp_details,
                        showRefuelDetails,
                        setShowRefuelDetails,
                        "primary",
                      )}
                      {renderPreRecordedList(
                        "Tollgate",
                        autoFetchedData?.toll_amnt,
                        autoFetchedData?.toll_exp_details,
                        showTollDetails,
                        setShowTollDetails,
                        "info",
                      )}
                      {renderPreRecordedList(
                        "Parking",
                        autoFetchedData?.parking_amnt,
                        autoFetchedData?.parking_exp_details,
                        showParkingDetails,
                        setShowParkingDetails,
                        "warning",
                      )}
                      {renderPreRecordedList(
                        "Night Halt",
                        autoFetchedData?.night_amnt,
                        autoFetchedData?.night_exp_details,
                        showNightDetails,
                        setShowNightDetails,
                        "secondary",
                      )}
                      {renderPreRecordedList(
                        "Food / Others",
                        autoFetchedData?.other_amnt,
                        autoFetchedData?.other_exp_details,
                        showFoodDetails,
                        setShowFoodDetails,
                        "danger",
                      )}
                    </div>
                  </div>
                )}

                <div className="mt-2">
                  <label className="native-label mb-2 text-dark">
                    Collection From Guest
                  </label>
                  <div className="input-icon-wrapper">
                    <IndianRupee
                      size={18}
                      className="input-icon text-primary"
                    />
                    <input
                      type="number"
                      className="form-control native-input input-with-icon w-100"
                      name="from_guest"
                      placeholder="0.00"
                      value={form.from_guest}
                      onChange={handleChange}
                      disabled={!autoFetchedData}
                      inputMode="decimal"
                      onWheel={preventScrollChange}
                    />
                  </div>
                </div>
              </div>
              {/* ODOMETER & FUEL CARD */}
              <div className="native-card p-3">
                <div className="d-flex align-items-center gap-3 mb-2 pb-3 border-bottom border-light">
                  <div className="icon-soft bg-warning bg-opacity-10 text-warning">
                    <MapPin size={20} strokeWidth={2.5} />
                  </div>
                  <h6
                    className="mb-0 fw-bold text-dark"
                    style={{ fontSize: "17px" }}
                  >
                    Odometer & Fuel
                  </h6>
                </div>

                <div className="row g-3 mb-4">
                  <div className="col-6">
                    <label className="native-label">Start Odo (KM)</label>
                    <input
                      type="number"
                      className="form-control native-input w-100"
                      name="start_km"
                      placeholder="0"
                      value={form.start_km}
                      onChange={handleChange}
                      required
                      disabled={!autoFetchedData}
                      readOnly
                      inputMode="decimal"
                      onWheel={preventScrollChange}
                    />
                  </div>
                  <div className="col-6">
                    <label className="native-label">End Odo (KM)</label>
                    <input
                      type="number"
                      className="form-control native-input w-100"
                      name="end_km"
                      placeholder="0"
                      value={form.end_km}
                      onChange={handleChange}
                      required
                      disabled={!autoFetchedData}
                      readOnly
                      inputMode="decimal"
                      onWheel={preventScrollChange}
                    />
                  </div>
                </div>

                <div className="row g-2 mb-4">
                  <div className="col-4">
                    <label className="native-label text-center">Total KM</label>
                    <input
                      type="text"
                      className="form-control native-input bg-transparent border-0 text-dark w-100 fw-bolder px-1 text-center"
                      value={calc.total_km}
                      disabled
                    />
                  </div>
                  <div className="col-4">
                    <label className="native-label text-center">Mileage</label>
                    <input
                      type="text"
                      className="form-control native-input bg-transparent border-0 text-muted w-100 px-1 text-center"
                      value={autoFetchedData ? displayData.millege : "---"}
                      disabled
                    />
                  </div>
                  <div className="col-4">
                    <label className="native-label text-center text-primary">
                      Oil Rate (₹)
                    </label>
                    <input
                      type="number"
                      className="form-control native-input w-100 px-1 text-center text-primary fw-bolder"
                      style={{ backgroundColor: "#e8f2ff" }}
                      name="oil_rate"
                      placeholder="0.0"
                      value={form.oil_rate}
                      onChange={handleChange}
                      disabled={!autoFetchedData}
                      inputMode="decimal"
                      onWheel={preventScrollChange}
                    />
                  </div>
                </div>

                <div
                  className="d-flex justify-content-between align-items-center p-3 rounded-4"
                  style={{ backgroundColor: "#fef2f2" }}
                >
                  <div className="d-flex flex-column">
                    <span className="native-label text-danger mb-1">
                      Calculated Fuel
                    </span>
                    {autoFetchedData && (
                      <span
                        className="text-danger opacity-75 fw-medium"
                        style={{ fontSize: "12px" }}
                      >
                        ({calc.total_km}km ÷ {displayData.millege}) × ₹
                        {form.oil_rate || 0}
                      </span>
                    )}
                  </div>
                  <span className="fw-bolder text-danger fs-3">
                    ₹ {calc.fuel_amnt}
                  </span>
                </div>
              </div>
              {/* DRIVER CASH EXPENSES CARD  */}
              <div className="native-card p-3">
                <div className="d-flex align-items-center gap-3 mb-2">
                  <div className="icon-soft bg-danger bg-opacity-10 text-danger">
                    <Receipt size={20} strokeWidth={2.5} />
                  </div>
                  <h6
                    className="mb-0 fw-bold text-dark"
                    style={{ fontSize: "17px" }}
                  >
                    Driver Cash Expenses
                  </h6>
                </div>

                {!autoFetchedData?.driver_cash_fuel_amnt &&
                !autoFetchedData?.driver_cash_toll_amnt &&
                !autoFetchedData?.driver_cash_parking_amnt &&
                !autoFetchedData?.driver_cash_night_amnt &&
                !autoFetchedData?.driver_cash_other_amnt ? (
                  <p className="text-muted small mb-0">
                    No cash expenses recorded.
                  </p>
                ) : (
                  <div className="mb-2">
                    <span className="native-label text-danger mb-2">
                      Paid Out of Driver Pocket
                    </span>
                    <div className="p-1">
                      {renderPreRecordedList(
                        "Refuel (Cash)",
                        autoFetchedData?.driver_cash_fuel_amnt,
                        autoFetchedData?.driver_cash_fuel_details,
                        showCashFuelDetails,
                        setShowCashFuelDetails,
                        "danger",
                      )}
                      {renderPreRecordedList(
                        "Tollgate (Cash)",
                        autoFetchedData?.driver_cash_toll_amnt,
                        autoFetchedData?.driver_cash_toll_details,
                        showCashTollDetails,
                        setShowCashTollDetails,
                        "danger",
                      )}
                      {renderPreRecordedList(
                        "Parking (Cash)",
                        autoFetchedData?.driver_cash_parking_amnt,
                        autoFetchedData?.driver_cash_parking_details,
                        showCashParkingDetails,
                        setShowCashParkingDetails,
                        "danger",
                      )}
                      {renderPreRecordedList(
                        "Night Halt (Cash)",
                        autoFetchedData?.driver_cash_night_amnt,
                        autoFetchedData?.driver_cash_night_details,
                        showCashNightDetails,
                        setShowCashNightDetails,
                        "danger",
                      )}
                      {renderPreRecordedList(
                        "Food / Others (Cash)",
                        autoFetchedData?.driver_cash_other_amnt,
                        autoFetchedData?.driver_cash_other_details,
                        showCashOtherDetails,
                        setShowCashOtherDetails,
                        "danger",
                      )}
                    </div>
                  </div>
                )}
              </div>
              {/* CUSTOM MISC EXPENSES CARD */}
              <div className="native-card p-3">
                <div className="d-flex align-items-center gap-3 mb-2">
                  <div className="icon-soft bg-info bg-opacity-10 text-info">
                    <Receipt size={20} strokeWidth={2.5} />
                  </div>
                  <h6
                    className="mb-0 fw-bold text-dark"
                    style={{ fontSize: "17px" }}
                  >
                    Miscellaneous Expenses
                  </h6>
                </div>

                {/* SINGLE TITLE ROW */}
                <div className="row g-3 mb-2">
                  <div className="col-7">
                    <label className="native-label mb-0">Expense Name</label>
                  </div>
                  <div className="col-5">
                    <label className="native-label mb-0">Amount</label>
                  </div>
                </div>

                {/* Custom Expense 1 */}
                <div className="row g-3 mb-3">
                  <div className="col-7">
                    <input
                      type="text"
                      className="form-control native-input w-100"
                      name="misc1"
                      placeholder="e.g. Extra Toll"
                      value={form.misc1}
                      onChange={handleChange}
                      disabled={!autoFetchedData}
                    />
                  </div>
                  <div className="col-5">
                    <div className="input-icon-wrapper">
                      <IndianRupee size={16} className="input-icon" />
                      <input
                        type="number"
                        className="form-control native-input input-with-icon w-100"
                        name="miscval1"
                        placeholder="0.00"
                        value={form.miscval1}
                        onChange={handleChange}
                        disabled={!autoFetchedData}
                        inputMode="decimal"
                        onWheel={preventScrollChange}
                      />
                    </div>
                  </div>
                </div>

                {/* Custom Expense 2 */}
                <div className="row g-3">
                  <div className="col-7">
                    <input
                      type="text"
                      className="form-control native-input w-100"
                      name="misc2"
                      placeholder="e.g. Car Wash"
                      value={form.misc2}
                      onChange={handleChange}
                      disabled={!autoFetchedData}
                    />
                  </div>
                  <div className="col-5">
                    <div className="input-icon-wrapper">
                      <IndianRupee size={16} className="input-icon" />
                      <input
                        type="number"
                        className="form-control native-input input-with-icon w-100"
                        name="miscval2"
                        placeholder="0.00"
                        value={form.miscval2}
                        onChange={handleChange}
                        disabled={!autoFetchedData}
                        inputMode="decimal"
                        onWheel={preventScrollChange}
                      />
                    </div>
                  </div>
                </div>
              </div>
              {/* FINAL SETTLEMENT (DARK NATIVE CARD) */}
              <div
                className="native-card border-0 mb-4"
                style={{ background: "#1c1c1e", color: "#f2f2f7" }}
              >
                <div className="card-body p-4">
                  {/* Collection Accordion */}
                  <div
                    className="d-flex justify-content-between align-items-center mb-3 user-select-none"
                    style={{ cursor: "pointer" }}
                    onClick={() =>
                      setShowCollectionDetails(!showCollectionDetails)
                    }
                  >
                    <span className="native-label text-light opacity-75 m-0">
                      Total Collection
                    </span>
                    <div className="d-flex align-items-center gap-2">
                      <span
                        className="text-success fw-bold"
                        style={{ fontSize: "16px" }}
                      >
                        + ₹ {calc.tot_frmguest}
                      </span>
                      {showCollectionDetails ? (
                        <ChevronUp size={18} className="text-secondary" />
                      ) : (
                        <ChevronDown size={18} className="text-secondary" />
                      )}
                    </div>
                  </div>

                  {showCollectionDetails && (
                    <div className="mb-3 ps-3 ms-1 border-start border-secondary accordion-content">
                      {parseFloat(autoFetchedData?.advance_amnt) > 0 && (
                        <div className="d-flex justify-content-between text-light small mb-2 opacity-75">
                          <span>Duty Advance</span>
                          <span>
                            ₹{" "}
                            {parseFloat(autoFetchedData.advance_amnt).toFixed(
                              2,
                            )}
                          </span>
                        </div>
                      )}
                      {parseFloat(autoFetchedData?.fuel_amnt) > 0 && (
                        <div className="d-flex justify-content-between text-light small mb-2 opacity-75">
                          <span>Refuel Amt.(Office)</span>
                          <span>
                            ₹ {parseFloat(autoFetchedData.fuel_amnt).toFixed(2)}
                          </span>
                        </div>
                      )}
                      {parseFloat(autoFetchedData?.toll_amnt) > 0 && (
                        <div className="d-flex justify-content-between text-light small mb-2 opacity-75">
                          <span>Tollgate Amt.(Office)</span>
                          <span>
                            ₹ {parseFloat(autoFetchedData.toll_amnt).toFixed(2)}
                          </span>
                        </div>
                      )}
                      {parseFloat(autoFetchedData?.parking_amnt) > 0 && (
                        <div className="d-flex justify-content-between text-light small mb-2 opacity-75">
                          <span>Parking Amt.(Office)</span>
                          <span>
                            ₹{" "}
                            {parseFloat(autoFetchedData.parking_amnt).toFixed(
                              2,
                            )}
                          </span>
                        </div>
                      )}
                      {parseFloat(autoFetchedData?.night_amnt) > 0 && (
                        <div className="d-flex justify-content-between text-light small mb-2 opacity-75">
                          <span>Night Halt Amt.(Office)</span>
                          <span>
                            ₹{" "}
                            {parseFloat(autoFetchedData.night_amnt).toFixed(2)}
                          </span>
                        </div>
                      )}
                      {parseFloat(autoFetchedData?.other_amnt) > 0 && (
                        <div className="d-flex justify-content-between text-light small mb-2 opacity-75">
                          <span>Food / Others Amt.(Office)</span>
                          <span>
                            ₹{" "}
                            {parseFloat(autoFetchedData.other_amnt).toFixed(2)}
                          </span>
                        </div>
                      )}
                      {parseFloat(form.from_guest) > 0 && (
                        <div className="d-flex justify-content-between text-light small mb-2 opacity-75">
                          <span>Guest Collection</span>
                          <span>
                            ₹ {parseFloat(form.from_guest).toFixed(2)}
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Expense Accordion */}
                  <div
                    className="d-flex justify-content-between align-items-center mb-3 mt-4 user-select-none"
                    style={{ cursor: "pointer" }}
                    onClick={() => setShowExpenseDetails(!showExpenseDetails)}
                  >
                    <span className="native-label text-light opacity-75 m-0">
                      Total Expenses
                    </span>
                    <div className="d-flex align-items-center gap-2">
                      <span
                        className="text-danger fw-bold"
                        style={{ fontSize: "16px" }}
                      >
                        - ₹ {calc.tot_amnt}
                      </span>
                      {showExpenseDetails ? (
                        <ChevronUp size={18} className="text-secondary" />
                      ) : (
                        <ChevronDown size={18} className="text-secondary" />
                      )}
                    </div>
                  </div>

                  {showExpenseDetails && (
                    <div className="mb-2 ps-3 ms-1 border-start border-secondary accordion-content">
                      {parseFloat(calc.fuel_amnt) > 0 && (
                        <div className="d-flex justify-content-between text-light small mb-2 opacity-75">
                          <span>Calculated Fuel</span>
                          <span>₹ {calc.fuel_amnt}</span>
                        </div>
                      )}

                      {/* CASH EXPENSE DISPLAYS */}

                      {parseFloat(autoFetchedData?.driver_cash_toll_amnt) >
                        0 && (
                        <div className="d-flex justify-content-between text-light small mb-2 opacity-75">
                          <span>Tollgate (Cash)</span>
                          <span>
                            ₹{" "}
                            {parseFloat(
                              autoFetchedData.driver_cash_toll_amnt,
                            ).toFixed(2)}
                          </span>
                        </div>
                      )}
                      {parseFloat(autoFetchedData?.driver_cash_parking_amnt) >
                        0 && (
                        <div className="d-flex justify-content-between text-light small mb-2 opacity-75">
                          <span>Parking (Cash)</span>
                          <span>
                            ₹{" "}
                            {parseFloat(
                              autoFetchedData.driver_cash_parking_amnt,
                            ).toFixed(2)}
                          </span>
                        </div>
                      )}
                      {parseFloat(autoFetchedData?.driver_cash_night_amnt) >
                        0 && (
                        <div className="d-flex justify-content-between text-light small mb-2 opacity-75">
                          <span>Night Halt (Cash)</span>
                          <span>
                            ₹{" "}
                            {parseFloat(
                              autoFetchedData.driver_cash_night_amnt,
                            ).toFixed(2)}
                          </span>
                        </div>
                      )}
                      {parseFloat(autoFetchedData?.driver_cash_other_amnt) >
                        0 && (
                        <div className="d-flex justify-content-between text-light small mb-2 opacity-75">
                          <span>Food/Other (Cash)</span>
                          <span>
                            ₹{" "}
                            {parseFloat(
                              autoFetchedData.driver_cash_other_amnt,
                            ).toFixed(2)}
                          </span>
                        </div>
                      )}

                      {/* Dynamic Custom Expenses */}
                      {parseFloat(form.miscval1) > 0 && (
                        <div className="d-flex justify-content-between text-light small mb-2 opacity-75">
                          <span>{form.misc1 || "Misc Expense 1"}</span>
                          <span>₹ {parseFloat(form.miscval1).toFixed(2)}</span>
                        </div>
                      )}
                      {parseFloat(form.miscval2) > 0 && (
                        <div className="d-flex justify-content-between text-light small mb-2 opacity-75">
                          <span>{form.misc2 || "Misc Expense 2"}</span>
                          <span>₹ {parseFloat(form.miscval2).toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                  )}
                  {parseFloat(autoFetchedData?.driver_cash_fuel_amnt) > 0 && (
                    <div className="border-top border-secondary border-opacity-50 pt-3 mt-4 d-flex justify-content-between align-items-center">
                      <span
                        className="native-label m-0"
                        style={{ fontSize: "12px", color: "#fbbf24" }}
                      >
                        * REFUEL AMT (CASH)
                      </span>
                      <span
                        className="fs-4 fw-bold tracking-tight"
                        style={{ color: "#fbbf24" }}
                      >
                        ₹{" "}
                        {parseFloat(
                          autoFetchedData.driver_cash_fuel_amnt,
                        ).toFixed(2)}
                      </span>
                    </div>
                  )}

                  {/* Final Payout */}
                  <div className="border-top border-secondary pt-4 mt-3 d-flex justify-content-between align-items-center">
                    <span
                      className="native-label text-white m-0"
                      style={{ fontSize: "13px" }}
                    >
                      PAID TO OFFICE
                    </span>
                    <span className="fs-1 fw-bold text-white tracking-tight">
                      ₹ {calc.paid_ofc}
                    </span>
                  </div>
                </div>
              </div>

              <button
                className="btn w-100 rounded-pill fw-bold fs-5 shadow-sm d-flex align-items-center justify-content-center gap-2 mb-4"
                style={{
                  padding: "16px 0",
                  transition: "transform 0.1s ease",
                  background: "linear-gradient(90deg, #007aff, #34c759)",
                  border: "none",
                  color: "white",
                }}
                type="submit"
                disabled={loading || !autoFetchedData}
                onMouseDown={(e) =>
                  (e.currentTarget.style.transform = "scale(0.97)")
                }
                onMouseUp={(e) =>
                  (e.currentTarget.style.transform = "scale(1)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.transform = "scale(1)")
                }
              >
                {loading ? (
                  <Loader2 className="spinner-border spinner-border-sm" />
                ) : (
                  <>
                    <Navigation size={22} /> Submit to Office
                  </>
                )}
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default DmrPage;